import { useReactiveVar } from '@apollo/client';
import useModal from '@area2k/use-modal';
import { useState, useMemo, useEffect, useCallback } from 'react';

import MyTeamFilters from './MyTeamFilters';
import AddNewMemberModal from './modals/addNewMemberModal';

import Avatar from '@/components/Avatar';
import Badge from '@/components/Badge';
import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Page from '@/components/Page';
import Paginator from '@/components/Paginator/Paginator';
import Stack from '@/components/Stack';
import SuccessfulCreateAlert from '@/components/SuccessfulCreationAlert';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import TextStack from '@/components/TextStack';
import { Body, Small } from '@/components/Typography';
import { TIME_TO_REFRESH } from '@/constants/general';
import Link from '@/elements/Link';
import { TableCell, TableRow } from '@/elements/Table';
import useMediaQuery from '@/hooks/useMediaQuery';
import ROUTES from '@/routes/routes';
import { User } from '@/types/graphql';
import { currentAdminVar } from '@/util/apollo/cache';
import { axiosRubyClient } from '@/util/axios/axiosClient';

export type MemberInvited = {
  invited: boolean;
  data: Pick<User, 'id' | 'firstName' | 'lastName'> | undefined;
};

type AdminUser = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string | null;
  active: boolean;
};

type CustomerAdmin = {
  id: number;
  accepted_at: string | null;
  avatar_url: string | null;
  user: AdminUser;
};

type AdminsResponse = {
  success: boolean;
  data: CustomerAdmin[];
  pagination: {
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
};

const MyTeam = () => {
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [admins, setAdmins] = useState<CustomerAdmin[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);

  const initialMemberInvited = {
    invited: false,
    data: {
      id: '',
      firstName: '',
      lastName: '',
    },
  };
  const [memberInvited, setMemberInvited] =
    useState<MemberInvited>(initialMemberInvited);

  const currentAdmin = useReactiveVar(currentAdminVar);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchAdmins = useCallback(async () => {
    if (!currentAdmin?.customer?.id) return;

    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: itemsPerPage.toString(),
        exclude_external: 'true',
      });
      if (debouncedQuery) params.append('query', debouncedQuery);

      const response = await axiosRubyClient.get<AdminsResponse>(
        `/api/admin/customers/${currentAdmin.customer.id}/admins?${params.toString()}`,
      );

      if (response.data.success) {
        setAdmins(response.data.data);
        setTotalItems(response.data.pagination.total);
      }
    } catch (err) {
      console.error('Error fetching team admins:', err);
    } finally {
      setLoading(false);
    }
  }, [currentAdmin?.customer?.id, currentPage, itemsPerPage, debouncedQuery]);

  useEffect(() => {
    fetchAdmins();
    const interval = setInterval(fetchAdmins, TIME_TO_REFRESH);
    return () => clearInterval(interval);
  }, [fetchAdmins]);

  const headerFields = useMemo(() => ['User', 'Phone number', 'Status'], []);

  const [showAddNewMemberModal, hideAddNewMemberModal] = useModal(
    () => (
      <AddNewMemberModal
        setMemberWasInvited={setMemberInvited}
        onHide={hideAddNewMemberModal}
      />
    ),
    [],
  );

  const [showSuccessfulCreateModal, hideSuccessfulCreateModal] = useModal(
    () => (
      <SuccessfulCreateAlert
        body="Your invitation has been successfully sent"
        hideModal={() => {
          hideSuccessfulCreateModal();
          setMemberInvited(initialMemberInvited);
        }}
        item={`${memberInvited.data!.firstName} ${
          memberInvited.data!.lastName
        }`}
      />
    ),
    [memberInvited],
  );

  const handlePageChange = useMemo(
    () => (pageNumber: number) => setCurrentPage(pageNumber),
    [],
  );

  const handleNumberItemsChange = useMemo(
    () => (event) => {
      setCurrentPage(1);
      setItemsPerPage(parseInt(event.target.value));
    },
    [],
  );

  useEffect(() => {
    if (memberInvited.invited) {
      fetchAdmins();
      return showSuccessfulCreateModal();
    }
  }, [memberInvited, showSuccessfulCreateModal]);

  return (
    <Page
      footer={
        admins.length > 0 && (
          <Paginator
            currentPage={currentPage}
            handleNumberItemsChange={handleNumberItemsChange}
            handlePageChange={handlePageChange}
            itemsLength={totalItems}
            itemsPerPage={itemsPerPage}
          />
        )
      }
      headerPadding={phoneOnly}
      noPadding={phoneOnly}
      primaryAction={{
        a11yLabel: 'Create a Admin',
        label: 'New Member',
        onAction: showAddNewMemberModal,
        id: 'new-admin-btn',
      }}
      size={phoneOnly ? 'full' : 'md'}
      title="My Team"
    >
      <Card noRadius={phoneOnly}>
        <Card.Section>
          <MyTeamFilters query={searchQuery} onQueryChange={setSearchQuery} />
        </Card.Section>

        <Table>
          {!phoneOnly && <TableHeader fields={headerFields} />}
          <tbody>
            {admins.map((admin) => (
              <TableRow
                key={admin.id}
                mobile={phoneOnly}
                spacing={phoneOnly ? 'normal' : false}
              >
                <TableCell noCellTitle={phoneOnly} stack={phoneOnly}>
                  <Stack gap={phoneOnly ? 10 : 8}>
                    <div>
                      <Avatar
                        firstName={admin.user.first_name}
                        size="sm"
                        src={admin.avatar_url}
                      />
                    </div>
                    <TextStack spacing={phoneOnly ? 'loose' : 'normal'}>
                      <Link
                        to={{
                          pathname:
                            String(admin.id) === String(currentAdmin!.id)
                              ? `../${ROUTES.myProfile}`
                              : `../my-team/${admin.id}`,
                        }}
                      >
                        <Body color="inherit" weight="medium">
                          {admin.user.first_name} {admin.user.last_name}
                        </Body>
                      </Link>

                      <Link
                        as="a"
                        href={`mailto:${admin.user.email}`}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <Small size={phoneOnly ? 'md' : 'sm'}>
                          {admin.user.email}
                        </Small>
                      </Link>
                      {phoneOnly && (
                        <Link
                          as="a"
                          href={`tel:${admin.user.phone_number}`}
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          <Small color={'default'} size="md">
                            {admin.user.phone_number}
                          </Small>
                        </Link>
                      )}
                    </TextStack>
                  </Stack>
                </TableCell>
                {!phoneOnly && (
                  <TableCell stack={phoneOnly}>
                    {admin.user.phone_number}
                  </TableCell>
                )}
                <TableCell noCellTitle={phoneOnly} stack={phoneOnly}>
                  <Badge
                    label={
                      admin.accepted_at
                        ? admin.user.active
                          ? 'Enabled'
                          : 'Disabled'
                        : 'Pending'
                    }
                    status={
                      admin.accepted_at
                        ? admin.user.active
                          ? 'theme'
                          : 'danger'
                        : 'warning'
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
            {loading && (
              <TableRow>
                <TableCell align="center" colSpan={headerFields.length + 1}>
                  <LoadingState overlayColor="white" />
                </TableCell>
              </TableRow>
            )}
          </tbody>
        </Table>
        {!loading && admins.length === 0 && (
          <Card.Section>
            <Stack style={{ justifyContent: 'center', padding: '20px' }}>
              <Small color="lighter">
                No client admins were found, invite one above.
              </Small>
            </Stack>
          </Card.Section>
        )}
      </Card>
    </Page>
  );
};

export default MyTeam;
